import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, KeyRound } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { PageHead, Modal, Field, Sel, inp } from './components';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../lib/shop';

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'admin' });
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get('/admin/administrators');
      setAdmins(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/admin/administrators', form);
      toast.success('Administrator saved');
      setOpen(false);
      setForm({ email: '', password: '', name: '', role: 'admin' });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/admin/administrators/${u.user_id}`, { role });
      toast.success('Role updated');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!pw) return;
    try {
      await api.put(`/admin/administrators/${u.user_id}`, { password: pw });
      toast.success('Password updated');
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-admins">
      <PageHead title="Administrators" sub="Who can access this dashboard. Only the owner can manage administrators.">
        {user?.role === 'owner' && (
          <button data-testid="add-admin" onClick={() => setOpen(true)}
            className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#E54D2E] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add admin
          </button>
        )}
      </PageHead>
      <div className="border hairline bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b hairline">{['Name', 'Email', 'Role', 'Since', ''].map((h) => <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>)}</tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id} className="border-b hairline last:border-0">
                <td className="p-3 font-semibold">{a.name}</td>
                <td className="p-3 font-mono2 text-xs">{a.email}</td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-0.5">{a.role}</span></td>
                <td className="p-3 text-xs text-[#8A8F95]">{fmtDate(a.created_at)}</td>
                <td className="p-3">
                  {user?.role === 'owner' && (
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`admin-resetpw-${a.user_id}`} onClick={() => resetPassword(a)} className="p-1.5 hover:bg-[#F4F4F0]" title="Reset password"><KeyRound className="w-4 h-4" /></button>
                      {a.role === 'admin' && (
                        <button data-testid={`admin-demote-${a.user_id}`} onClick={() => setRole(a, 'customer')}
                          className="border hairline px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-widest text-[#E54D2E] hover:border-[#E54D2E]">Revoke</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title="Add Administrator" onClose={() => setOpen(false)}>
          <div className="space-y-4">
            <Field label="Email"><input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
            <Field label="Name"><input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
            <Field label="Password (min 8 chars)"><input data-testid="new-admin-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} autoComplete="new-password" /></Field>
            <Sel label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={[['admin', 'Admin'], ['owner', 'Owner']]} testid="new-admin-role" />
            <button data-testid="new-admin-save" onClick={create} className="w-full bg-[#0B0F12] text-white py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors">Create administrator</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
